import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, linkOptions, Outlet } from "@tanstack/react-router";
import z from "zod";
import { SiteHeader } from "@/components/site-header";
import { TabBar, type TabBarTabs } from "@/components/tab-bar";
import { Tabs } from "@/components/ui/tabs";
import { orpc } from "@/orpc/client";

export const Route = createFileRoute("/_app/publishers/$publisherId")({
	component: Page,
	params: z.object({
		publisherId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.publishers.show.queryOptions({
				input: {
					publisherId: params.publisherId,
				},
			}),
		);

		// Preload users for the UserCombobox
		context.queryClient.prefetchQuery(
			orpc.users.list.queryOptions({
				input: {},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data } = useSuspenseQuery(
		orpc.publishers.show.queryOptions({
			input: {
				publisherId: params.publisherId,
			},
		}),
	);

	const tabs: TabBarTabs = linkOptions([
		{
			key: "details",
			to: "/publishers/$publisherId",
			params: { publisherId: data.ID },
			activeOptions: { exact: true },
			label: "Details",
		},
		{
			key: "channels",
			to: "/publishers/$publisherId/channels",
			params: { publisherId: data.ID },
			label: "Channels",
			count: data.channelsCount,
		},
		{
			key: "users",
			to: "/publishers/$publisherId/users",
			params: { publisherId: data.ID },
			label: "Users",
			count: data.usersCount,
		},
		{
			key: "settings",
			to: "/publishers/$publisherId/settings",
			params: { publisherId: data.ID },
			label: "Settings",
		},
	]);

	return (
		<>
			<SiteHeader
				breadcrumbs={[
					{
						key: "publishers",
						label: "Publishers",
						linkProps: { to: "/publishers" },
					},
					{
						key: "publisher",
						label: data.PublisherName ?? "Publisher Details",
					},
				]}
			/>
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-1 flex-col gap-4 py-4 md:gap-6 md:py-6">
						<div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
							<div className="flex w-full flex-col justify-between gap-2 md:flex-row md:gap-4 lg:items-end">
								<div className="flex gap-3">
									<div>
										<h1 className="text-lg leading-tight font-semibold">{data.PublisherName}</h1>
										<p className="text-muted-foreground text-sm">ID: #{data.ID}</p>
									</div>
								</div>
							</div>
						</div>
						<Tabs>
							<div className="px-4 lg:px-6">
								<TabBar tabs={tabs} />
							</div>
							<Outlet />
						</Tabs>
					</div>
				</div>
			</div>
		</>
	);
}
